import { JSX, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { ExportFormatType } from "@app/exports/enumerations";
import { ExportNetworkIntegrityProviderReportModel } from "@app/exports/exportModels";
import { reportName as reportNameHelper } from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import { SelectFormat } from "@app/exports/export-panel-body/filters";
import * as toast from "@app/exports/export-panel-body/toast";
import { NetworkIntegrityAggregateGroup, NetworkIntegrityAggregateKey } from "@app/projects/enumerators";
import { GenerateBody } from "@app/projects/network-assessment/network-integrity/exports/GenerateBody";
import { NetworkIntegrityRouteState, RouteMatchParams } from "@app/projects/network-assessment/network-integrity/models";
import { NetworkIntegrityProviderTypes } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import ExportService from "@app/exports/exportService";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";

const getFileNameAggregateType = (aggregateType: string, aggregateGroup: string): string => {
  if(aggregateGroup === NetworkIntegrityAggregateGroup.ProvidersBySpecialty)
    return "Providers By Specialty";

  switch (aggregateType) {
    case NetworkIntegrityAggregateKey.DeactivatedNpi:
      return "Deactivated NPI";
    case NetworkIntegrityAggregateKey.InvalidNpi:
      return "Invalid NPI";
    case NetworkIntegrityAggregateKey.MedicarePrecluded:
      return "Medicare Preclusion";
    case NetworkIntegrityAggregateKey.MissingNpi:
      return "Missing NPI";
    case NetworkIntegrityAggregateGroup.AttestationBasedAlerts:
      return "Attestation-based Alerts";
    case NetworkIntegrityAggregateGroup.AllExcessWarnings:
      return "Excess Warnings"
    case NetworkIntegrityAggregateGroup.ExcessAddresses:
      return "Excess Addresses"
    case NetworkIntegrityAggregateGroup.ExcessSpecialties:
      return "Excess Specialties";
    case NetworkIntegrityAggregateGroup.CriticalAlerts:
      return "Critical Alerts";
    case NetworkIntegrityAggregateGroup.OptionalExclusions:
      return "Project Option Exclusions";
    default:
      return aggregateType;
  };
}

const getIsServicingAndNetworkType = (providerType: string): {  isServicing: boolean, providerNetworkType: ProviderNetworkTypeEnum } => {
  switch (providerType) {
    case NetworkIntegrityProviderTypes.Servicing:
      return { isServicing: true, providerNetworkType: ProviderNetworkTypeEnum.Network };
    case NetworkIntegrityProviderTypes.ProspectServicing:
      return { isServicing: true, providerNetworkType: ProviderNetworkTypeEnum.Prospect };
    case NetworkIntegrityProviderTypes.Prospect:
      return { isServicing: false, providerNetworkType: ProviderNetworkTypeEnum.Prospect };
    default:
      return { isServicing: false, providerNetworkType: ProviderNetworkTypeEnum.Network };
  }
}

const NetworkIntegrityGenerateReportPanelBody = (props: RouteMatchParams): JSX.Element => {

  const exportService = new ExportService();
  const locationState = useLocation<NetworkIntegrityRouteState>().state;
  const allOrSelected = (locationState.selectedKeys && locationState.selectedKeys.length > 0) ? "Selected" : "All";
  const defaultReportName = reportNameHelper(`Provider Detail - ${allOrSelected} ${props.providerType} Providers - ${getFileNameAggregateType(props.aggregateType ?? props.aggregateGroup, props.aggregateGroup)}`);

  const [emailOnSuccess, setEmailOnSuccess] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [outputFormat, setOutputFormat] = useState<ExportFormatType>(ExportFormatType.CSV);
  const [reportName, setReportName] = useState<string>(defaultReportName);
  const [sharedUsers, setSharedUsers] = useState<number[]>([]);
  const [includeCargoColumns, setIncludeCargoColumns] = useState<boolean>(false);
  const exportDataRowCount = locationState.reportRowCount;
  const excelRowLimit = 1048570; // Excel row limit is 1048576, limiting to just under that to allow for header rows

  // Event Handlers
  const handleEmailOnSuccessChange = (value: boolean) => {
    setEmailOnSuccess(value);
  };

  const handleIncludeCargoColumnsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeCargoColumns(e.target.checked);
  };

  const handleGenerateClick = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    const {  isServicing,  providerNetworkType } = getIsServicingAndNetworkType(props.providerType);

    const requestModel : ExportNetworkIntegrityProviderReportModel = {
      aggregateGroup: props.aggregateGroup,
      aggregateKey: props.aggregateType,
      eMailOnSuccess: emailOnSuccess,
      exportName: reportName,
      formatType: outputFormat,
      includeCargoColumns: includeCargoColumns,
      isServicing,
      npiEntityType: locationState.npiEntityType ?? null,
      projectId: Number.parseInt(props.projectId),
      projectRunConfigurationId: Number.parseInt(props.projectRunConfigurationId),
      providerNetworkType,
      runAsynchronous: true,
      selectedKeys: locationState.selectedKeys,
      selectedProviderAndProviderSetVersionIds: locationState.selectedProviderAndProviderSetVersionIds,
      showConfidenceLevel: locationState.showConfidenceLevel,
    }

    try {
      await exportService.postExportNetworkIntegrityProviders(requestModel);
      toast.toastInfo(reportName);
    } catch {
      setError("An error occurred while generating the report. Please try again.");
    }
  }, [props.projectId, props.projectRunConfigurationId, props.providerType, outputFormat, sharedUsers, reportName, emailOnSuccess, includeCargoColumns]);

  const handleReportNameChange = useCallback((value: string) => {
    setReportName(value);
  }, [props.projectId, props.projectRunConfigurationId])

  const handleSharedUserChange = useCallback((users: number[]) => {
    setSharedUsers(users);
  }, [props.projectId, props.projectRunConfigurationId]);

  const handleOutputFormatChange = useCallback((value: ExportFormatType) => {
    setOutputFormat(value);
  }, [props.projectId, props.projectRunConfigurationId]);

  // Render
  return (
    <>
    <div className="row">
      {!!error && <QESAlert classNames="col-12" type="danger" persist={true}>{error}</QESAlert>}
    </div>
    <div className="row">
      <div className="col-6 col-sm-6">
        <h5>Column Configuration</h5>
          <QESCheckbox
            checked={includeCargoColumns}
            testId="network-integrity-report-body-cargo-column-select"
            label="Include Cargo Columns"
            onChange={handleIncludeCargoColumnsChange}/>
      </div>
      <div className="col-6 col-sm-3">
        <SelectFormat
          excelDisabled={exportDataRowCount > excelRowLimit}
          exportType="network-integrity"
          outputFormat={outputFormat}
          onOutputFormatChange={handleOutputFormatChange} />
        {exportDataRowCount > excelRowLimit && <p className="text-danger"><i>Results are too large for Microsoft Excel Workbook (.xlsx).</i></p>}
      </div>
    </div>
    <GenerateBody
        {...props}
        emailOnSuccess={emailOnSuccess}
        isLoading={isLoading}
        onEmailOnSuccessChange={handleEmailOnSuccessChange}
        onGenerateClick={handleGenerateClick}
        onReportNameChange={handleReportNameChange}
        onSharedUsersChange={handleSharedUserChange}
        reportName={reportName}
        sharedUsers={sharedUsers}
      />
    </>
  );
}

export { NetworkIntegrityGenerateReportPanelBody };